/**
 * nba.helpers — leg derivation for the NBA (order status) card.
 *
 * The card pages through legs WAL-style (arrow navigation), defaulting to the leg the
 * freight is physically on.
 */

/**
 * Statuses meaning the freight has PHYSICALLY finished a leg. Mirrors the backend's
 * PHYSICALLY_DONE_LEG_STATUSES (orderDetail.service.ts) / WAL's radar rule — a leg parked
 * at `delivered` awaiting paperwork must not hold the active pointer while the truck is
 * already on the next leg.
 */
const PHYSICALLY_DONE_STATUSES = ['delivered', 'claim', 'dispute', 'invoice-created', 'invoice-sent', 'invoice-paid', 'completed', 'cancelled'];

export const normalizeLegStatus = (status?: string | null): string =>
    String(status || '')
        .toLowerCase()
        .replace(/_/g, '-');

export const isLegPhysicallyDone = (status?: string | null): boolean => PHYSICALLY_DONE_STATUSES.includes(normalizeLegStatus(status));

/** "4 pallets were" / "1 pallet was" for item phrases from buildItemsPhrase. Lives here so the pure wording helpers stay import-free. */
export const withItemsVerb = (items: string, singular: string, plural: string): string => `${items} ${/^1\s/.test(items) ? singular : plural}`;

/** Live legs (not deleted, not cancelled) in route order. */
export const getLiveLegsSorted = (legs?: any[]): any[] =>
    [...(legs ?? [])].filter((leg) => !leg?.isDeleted && normalizeLegStatus(leg?.orderStatus) !== 'cancelled').sort((a, b) => (a?.legPosition ?? 0) - (b?.legPosition ?? 0));

/**
 * The leg the freight is physically on. Prefers the backend's isActiveLeg flag (same rule
 * server-side); falls back to computing it locally so stale payloads still resolve sanely.
 */
export const getPhysicallyActiveLeg = (legs?: any[]): any | null => {
    const liveLegs = getLiveLegsSorted(legs);
    if (liveLegs.length === 0) return null;

    const flagged = liveLegs.find((leg) => leg?.isActiveLeg === true);
    if (flagged) return flagged;

    const stillMoving = liveLegs.find((leg) => !isLegPhysicallyDone(leg?.orderStatus));

    return stillMoving ?? liveLegs[liveLegs.length - 1];
};

/**
 * Leg and stop numbering the way WALHQ reads it (multi-leg wording, 2026-09-04). A LEG is one carrier
 * engagement: a same-truck run (a leg plus every leg covered by it, `carrierSourceLegId` /
 * `carrierPrice.isCoveredCarrier`) counts once. A STOP is a call within a Leg; a Leg's stop count is
 * its segment count plus one, and each presented leg is the stop it departs from. Mirrors
 * wal-frontend-v2 src/app/Orders/order.legNumbering.ts so the two footers agree.
 */
interface ILegNumbering {
    legNumber: number;
    legCount: number;
    stopNumber: number | null;
    stopCount: number | null;
    isMultiLeg: boolean;
}

const isCoveredLeg = (leg: any): boolean => leg?.carrierSourceLegId != null || !!leg?.carrierPrice?.isCoveredCarrier;

/**
 * Portal visibility setting "Display Order Legs" (`showAsMultiLeg`), resolved per order by the
 * portal backend and returned as `order.orderVisibility`. When it is not true the backend has
 * already merged WAL-internal junctions, so the presented legs are the customer's own stops
 * and the card must never say "leg", quote a leg id or mention a carrier change.
 */
export const isLegsDisplayed = (order: any): boolean => order?.orderVisibility?.showAsMultiLeg === true;

/**
 * Card footer position. Legs displayed: "Leg 1 of 3 · Stop 1 of 3 (#WAL-XXXX-A)" like WALHQ.
 * Legs hidden: "Stop 1 of 3", where each presented leg is one stop and the total is the number
 * of pages the header arrows can reach. The total must NOT be legCount + 1: the pager stops on
 * the last presented leg, so an extra "final delivery" stop would leave the last page reading
 * "Stop 3 of 4" with nowhere to go.
 */
export const getCardFooterLabel = (params: { legsDisplayed: boolean; numbering: ILegNumbering | null; viewingIndex: number; legCount: number; legId?: string | null }): string => {
    const { legsDisplayed, numbering, viewingIndex, legCount, legId } = params;

    if (!legsDisplayed) return `Stop ${viewingIndex + 1} of ${legCount}`;

    const position = numbering
        ? `Leg ${numbering.legNumber} of ${numbering.legCount}${numbering.stopNumber ? ` · Stop ${numbering.stopNumber} of ${numbering.stopCount}` : ''}`
        : `Stop ${viewingIndex + 1} of ${legCount}`;

    return legId ? `${position} (#${legId})` : position;
};

export const getLegNumbering = (leg: any, liveLegs: any[]): ILegNumbering | null => {
    if (!leg || !liveLegs?.length) return null;
    const groups: any[][] = [];
    liveLegs.forEach((item: any) => {
        // A covered leg rides the previous engagement's truck; anything else starts a new Leg.
        if (isCoveredLeg(item) && groups.length > 0) groups[groups.length - 1].push(item);
        else groups.push([item]);
    });
    const index = groups.findIndex((members) => members.some((m: any) => m?.id === leg?.id));
    if (index === -1) return null;
    const members = groups[index];
    const isDirect = members.length <= 1;

    return {
        legNumber: index + 1,
        legCount: groups.length,
        stopNumber: isDirect ? null : members.findIndex((m: any) => m?.id === leg?.id) + 1,
        stopCount: isDirect ? null : members.length + 1,
        isMultiLeg: liveLegs.length > 1,
    };
};
